<template>
  <input
    :id="$attrs.id"
    ref="input"
    :class="$attrs.class"
    :disabled="$attrs.disabled"
    :placeholder="placeholder"
    :value="modelCompatValue"
    type="text"
    autocomplete="off"
    role="combobox"
    @input="onInput"
    @blur="$emit('blur', $event)"
  >
</template>

<script>
import VModelCompat from '~/mixins/v_model_compat';

// vue2-google-maps loaded the Maps JS API (with `places`) globally at app init; vue3-google-map only
// injects it when a `<GmapMap>` mounts. A standalone autocomplete (e.g. the member dialog, which has
// no map) must load it itself. Resolves once per page, reusing a script another component may have
// already added (vue3-google-map loads `places` too, so a shared script is enough).
let placesApiPromise = null;

function loadPlacesApi(apiKey) {
  if (window.google?.maps?.places) {
    return Promise.resolve();
  }

  if (!placesApiPromise) {
    placesApiPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

      if (!existing && !apiKey) {
        reject(new Error('Google Maps API key is missing'));

        return;
      }

      const script = existing || document.createElement('script');

      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => reject(new Error('Failed to load the Google Maps API')), { once: true });

      if (!existing) {
        script.async = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
        document.head.appendChild(script);
      }
    }).catch((error) => {
      placesApiPromise = null; // allow a later mount to retry
      throw error;
    });
  }

  return placesApiPromise;
}

// Vue 3 replacement for `vue2-google-maps`'s Autocomplete. Wraps
// `google.maps.places.Autocomplete` and supports both the legacy Vue 2 API
// (`:value` + native `@input`) and Vue 3 `v-model`, via the VModelCompat mixin
// (`modelCompatValue` resolves to whichever of `value`/`modelValue` the consumer passed).
export default {
  name: 'GmapAutocomplete',
  mixins: [VModelCompat],
  // The GoogleMaps plugin provides the key; needed to load Places when no map is on the page.
  inject: { googleMapsKey: { default: null } },
  inheritAttrs: false,
  props: {
    // `value` (Vue 2 compat) comes from VModelCompat; `modelValue` powers Vue 3 `v-model`.
    modelValue: { type: String, default: undefined },
    placeholder: { type: String, default: '' },
    types: { type: Array, default: () => [] },
    componentRestrictions: { type: Object, default: null },
    fields: { type: Array, default: () => ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'] },
    selectFirstOnEnter: { type: Boolean, default: false },
  },
  // VModelCompat also declares `update:modelValue` and `input`.
  emits: ['place_changed', 'blur'],
  watch: {
    // Sync the input when the bound value changes programmatically (e.g. picking a place sets the
    // formatted address upstream). Without binding to `modelCompatValue` the input would otherwise
    // reset on every render, which is what broke typing for `v-model` consumers.
    modelCompatValue(next) {
      const el = this.$refs.input;

      if (el && el.value !== next) {
        el.value = next ?? '';
      }
    },
  },
  async mounted() {
    // vue3-google-map's <GoogleMap> injects the Maps script in ITS onMounted. When this input is an
    // earlier sibling (e.g. the job sites search above the map) our mounted runs first, sees no
    // script and would inject a second one ("You have included the Google Maps JavaScript API
    // multiple times"). Waiting one tick lets every GoogleMap on the page inject first, so
    // loadPlacesApi reuses that script; standalone pages (no map) are unaffected.
    await this.$nextTick();

    try {
      await loadPlacesApi(this.googleMapsKey);
    } catch {
      return;
    }

    // The component may have unmounted while the API loaded; buildAutocomplete bails if so.
    this.buildAutocomplete();
  },
  beforeUnmount() {
    if (this.placeListener) {
      this.placeListener.remove();
    }
  },
  methods: {
    onInput(event) {
      // Emit the native event for legacy `@input` consumers, and the value for `v-model`.
      this.$emit('input', event);
      this.$emit('update:modelValue', event.target.value);
    },
    buildAutocomplete() {
      const el = this.$refs.input;

      if (!el || !window.google?.maps?.places?.Autocomplete) {
        return;
      }

      // Non-reactive instance refs (kept off `data()` so Vue doesn't proxy the Google objects).
      this.autocomplete = new window.google.maps.places.Autocomplete(el, {
        types: this.types?.length ? this.types : undefined,
        componentRestrictions: this.componentRestrictions || undefined,
        fields: this.fields,
      });

      this.placeListener = this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();

        if (place) {
          this.$emit('place_changed', place);
        }
      });

      if (this.selectFirstOnEnter) {
        // Mirror vue2-google-maps behaviour: Enter accepts the top suggestion
        // when the user hasn't navigated the dropdown.
        el.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') {
            return;
          }

          const suggestion = document.querySelector('.pac-container .pac-item');

          if (suggestion && !document.querySelector('.pac-item-selected')) {
            event.preventDefault();
            const text = suggestion.querySelector('.pac-item-query')?.textContent || '';

            suggestion.click?.();
            el.value = text;
            el.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });
      }
    },
  },
};
</script>
